import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { ReadOnlyHoverLayer, PageTitleWithHelp, CardTitleWithHelp } from "@/components/ui/title-help";
import { useLaborActiveSessionContext } from "@/components/LaborActiveSessionProvider";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { FINAL_QC_LABOR_PAGE } from "@/lib/labor/laborPageKey";
import { openLaborSessionIdForWirePage } from "@/lib/labor/openLaborSessionIdForWirePage";
import { playFailureSoundAndNavigate } from "@/lib/labor/laborTimeoutHandlers";
import { playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { useQcIncidentModal } from "@/lib/qc/qcIncidentModalContext";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import {
  FLOOR_SCAN_INPUT_FIELD_PROPS,
  floorSerialInputHasScannerTerminator,
  handleFloorSerialEnterKey,
  normalizeFloorSerialInput,
  readFloorSerialFromInput,
} from "@/lib/floorSerialScan";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";
import { skuRequiresImeiIccid } from "@integrator-src/manufacturing/configuration-sku-family";

import {
  fetchFinalQcDevice,
  fetchFinalQcFailureReasons,
  fetchFinalQcWirelessChecksList,
  failFinalQc,
  failFinalQcGpsCheck,
  passFinalQc,
  visualPassFinalQc,
  postFinalQcWirelessHitsSync,
  removeFinalQcWirelessCheck,
  watchFinalQcWirelessCheck,
  FINAL_QC_WIRELESS_CHECKS_QUERY_KEY,
  type FinalQcGpsCheckFailResult,
  type FinalQcWirelessCheck,
  type FinalQcWirelessChecksListResponse,
  type FailureReason,
  type FinalQcDevice,
  type FinalQcResult,
  type PottingGrade,
  type WirelessHitsSyncInfo,
} from "./finalQcApi";
import { DeviceLookupModalTrigger } from "./DeviceLookupModal";
import {
  GPS_CHECK_QC_INCIDENT_ATTEMPT_THRESHOLD,
  GpsCheckElapsed,
  gpsCheckRemainingMs,
  useNowMs,
} from "./GpsCheckElapsed";
import { FinalQcGpsIncidentModal } from "./FinalQcGpsIncidentModal";
import { FinalQcGpsOverrideModal } from "./FinalQcGpsOverrideModal";

const FINAL_QC_PAGE_HELP =
  "Hub Final QC mirrors the Appsmith pass and fail flow for SGS devices. For wireless stock keeping units, scanning a device starts Final QC and enqueues GPS watch automatically (see docs/final-qc-wireless-workflow.md). Complete visual pass and a valid GPS hit, then Pass from the GPS checks table to assign shipment. Potting rework devices are graded on quality.potting_rework instead of opening another incident. If a device was passed by mistake and is already on a shipment, use Mistaken Final QC pass from the link under this title.";

function isFinalQcAlreadyPassedLookupError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.body == null || typeof error.body !== "object") {
    return false;
  }
  const reason = (error.body as Record<string, unknown>).reason;
  return (
    reason === "already_in_shipment" ||
    reason === "already_passed_alternate" ||
    reason === "already_passed"
  );
}

/** Touch-friendly primary actions (shared by desktop and floor tablets). */
const FINAL_QC_FLOOR_ACTION_CLASS = "min-h-12 text-base";

function resultMessage(result: FinalQcResult | null): string | null {
  if (!result) return null;
  switch (result.reason) {
    case "potting_rework_passed":
    case "alternate_passed":
    case "touch_sample_passed":
    case "passed": {
      const destination =
        result.reason === "touch_sample_passed"
          ? "sales (touch sample — no shipment)"
          : result.shipment_id != null
            ? `shipment ${result.shipment_id}`
            : "alternates";
      return `Check: passed. Move to: ${destination}`;
    }
    case "visual_passed":
      return "Check: passed. Move to: GPS checks";
    case "potting_rework_failed":
      return "Potting rework failed. The rework row was updated and remains visible in the queue.";
    case "failed":
      return "Device failed Final QC and was moved to QC hold.";
    default:
      return result.reason;
  }
}

function deviceSubtitle(device: FinalQcDevice): string {
  const parts = [
    device.touchSample ? "Touch sample" : null,
    device.finishedSku,
    device.firmwareVersion ? `Firmware ${device.firmwareVersion}` : null,
    device.batchId != null ? `Batch ${device.batchId}` : null,
  ].filter((value): value is string => value != null && value !== "");
  return parts.length > 0 ? parts.join(" · ") : "No finished SKU or batch linked.";
}

function needsPottingGrade(device: FinalQcDevice | null): boolean {
  return !!device && (device.requiresPottingGrade || device.pottingRework != null);
}

function isWirelessDevice(device: FinalQcDevice | null): boolean {
  if (!device || device.skipWirelessChecks === true) return false;
  return skuRequiresImeiIccid(device.finishedSku) || (device.imei ?? "").trim() !== "";
}

function FinalQcLaborTrackerShared() {
  const navigate = useNavigate();
  const labor = useLaborSession({
    page: FINAL_QC_LABOR_PAGE,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Final QC",
    autoStart: true,
    requirePersistedClockIn: true,
    onIdleTimeout: () => playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onPersistedSessionLost: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onSessionEndedRemotely: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onStopped: () => playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const finalQcLaborDevPanelSnapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Final QC",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, finalQcLaborDevPanelSnapshot);

  return null;
}

function FinalQcWorkflowPanel({
  device,
  writesAllowed,
  pottingGrade,
  setPottingGrade,
  isSaving,
  isResettingGpsWatch,
  watched,
  alreadyOnWatchList,
  visualPassed,
  onResetGpsWatch,
  onPass,
  onFail,
}: {
  device: FinalQcDevice | null;
  writesAllowed: boolean;
  pottingGrade: PottingGrade | null;
  setPottingGrade: (grade: PottingGrade | null) => void;
  isSaving: boolean;
  isResettingGpsWatch: boolean;
  watched: boolean;
  alreadyOnWatchList: boolean;
  visualPassed: boolean;
  onResetGpsWatch: () => void;
  onPass: () => void;
  onFail: () => void;
}) {
  const wireless = isWirelessDevice(device);
  const gradeRequired = needsPottingGrade(device);
  const passDisabled =
    !writesAllowed ||
    !device ||
    isSaving ||
    (gradeRequired && pottingGrade == null) ||
    (wireless && visualPassed);

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitleWithHelp hoverHint="For wireless stock keeping units, scanning the device enqueues GPS watch automatically. Pass visual inspection, wait for a valid GPS hit, then Pass from the GPS checks table. Use Reset GPS watch only after a factory reset. Potting rework devices need grade A, B, or C before pass.">
          Final QC checks
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-5">
        {wireless ? (
          <div className="space-y-2">
            <div
              className={
                alreadyOnWatchList
                  ? "rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                  : "rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
              }
            >
              {alreadyOnWatchList
                ? "Already on the GPS checks watch list. This scan did not reset the watch or clear progress."
                : watched
                  ? "GPS watch is active for this device."
                  : "Scanning this wireless device enqueues GPS watch automatically."}
            </div>
            <Button
              type="button"
              className={cn("w-full", FINAL_QC_FLOOR_ACTION_CLASS)}
              variant="secondary"
              disabled={!writesAllowed || isResettingGpsWatch || !device || !watched}
              isLoading={isResettingGpsWatch}
              onClick={onResetGpsWatch}
            >
              Reset GPS watch
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {device?.skipWirelessChecks === true
              ? "This ABB unit skips magnet reset and GPS checks. Pass assigns the shipment."
              : "GPS reset checks are only needed for wireless devices."}
          </div>
        )}

        {gradeRequired ? (
          <section className="space-y-2">
            <p className="text-sm font-semibold text-slate-900">Potting grade</p>
            <div className="grid grid-cols-1 gap-2">
              {(["A", "B", "C"] satisfies PottingGrade[]).map((grade) => (
                <Button
                  key={grade}
                  type="button"
                  variant={pottingGrade === grade ? "primary" : "secondary"}
                  className={FINAL_QC_FLOOR_ACTION_CLASS}
                  onClick={() => setPottingGrade(grade)}
                  disabled={!writesAllowed || !device || isSaving}
                >
                  {grade}
                </Button>
              ))}
              <Button
                type="button"
                variant="secondary"
                className={FINAL_QC_FLOOR_ACTION_CLASS}
                onClick={onFail}
                disabled={!writesAllowed || !device || isSaving}
              >
                Fail
              </Button>
            </div>
            {pottingGrade == null ? (
              <p className="text-xs text-amber-700">Choose A, B, or C before passing.</p>
            ) : null}
          </section>
        ) : null}

        <section className="space-y-2">
          <p className="text-sm font-semibold text-slate-900">Visual inspection</p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="secondary"
              className={FINAL_QC_FLOOR_ACTION_CLASS}
              disabled={passDisabled}
              onClick={onPass}
            >
              Pass
            </Button>
            <Button
              type="button"
              variant="secondary"
              className={cn(
                FINAL_QC_FLOOR_ACTION_CLASS,
                "border-rose-300 bg-rose-100 text-rose-950 hover:bg-rose-200/90 disabled:border-rose-200 disabled:bg-rose-50 disabled:text-rose-800/70",
              )}
              disabled={!writesAllowed || !device || isSaving}
              onClick={onFail}
            >
              Fail
            </Button>
          </div>
          {wireless ? (
            <p className="text-xs text-slate-500">
              {visualPassed
                ? "Visual inspection is recorded. Finish from the GPS checks table after the reset hit appears."
                : "Pass records visual inspection only. Shipment assignment happens from GPS checks after a valid hit."}
            </p>
          ) : null}
        </section>
      </CardBody>
    </Card>
  );
}

function suggestedGpsFailureMode(
  hit: FinalQcWirelessCheck["hit"] | undefined,
  catalog: FinalQcWirelessChecksListResponse["gpsFailureModes"] | undefined,
): { id: number; name: string } | null {
  if (hit == null || catalog == null) return null;
  const kind = hit === "other" ? "bad_coordinates" : hit === "bad" ? "no_coordinates" : "check_in_failing";
  const mode = catalog[kind];
  if (mode == null) return null;
  return { id: mode.id, name: mode.name };
}

function wirelessHitLabel(hit: FinalQcWirelessCheck["hit"]): string {
  switch (hit) {
    case "austin":
      return "Austin";
    case "other":
      return "Other area";
    case "bad":
      return "Bad GPS";
    case "none":
      return "No hit yet";
    default:
      return hit;
  }
}

function WirelessChecksTable({
  rows,
  hitsColumnPending,
  refreshButtonPending,
  isMutating,
  onRefresh,
  onPass,
  onGpsFail,
  onOpenGpsIncident,
  onOpenGpsOverride,
  onRemove,
}: {
  rows: FinalQcWirelessCheck[];
  hitsColumnPending: boolean;
  refreshButtonPending: boolean;
  isMutating: boolean;
  onRefresh: () => void;
  onPass: (serial: string) => void;
  onGpsFail: (serial: string) => void;
  onOpenGpsIncident: (serial: string) => void;
  onOpenGpsOverride: (serial: string) => void;
  onRemove: (serial: string) => void;
}) {
  const nowMs = useNowMs();
  return (
    <Card className="w-full self-stretch xl:max-w-2xl xl:self-start">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitleWithHelp hoverHint="Devices waiting for wireless reset or check-in before they can be passed from Final QC.">
            GPS checks
          </CardTitleWithHelp>
        </div>
        <Button
          type="button"
          variant="secondary"
          className={cn("max-xl:w-full", FINAL_QC_FLOOR_ACTION_CLASS)}
          onClick={onRefresh}
          disabled={refreshButtonPending}
        >
          {refreshButtonPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          Refresh
        </Button>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Serial</th>
                <th className="px-3 py-2 text-left">Visual</th>
                <th className="px-3 py-2 text-left">Hit</th>
                <th className="px-3 py-2 text-left">Checks</th>
                <th className="px-3 py-2 text-left">Pass</th>
                <th className="px-3 py-2 text-left">Fail</th>
                <th className="px-3 py-2 text-right">Remove</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const gpsCheck = row.gpsCheck ?? {
                  attemptCount: 0,
                  lastCheckStartedAt: null,
                  passed: false,
                  passSource: null,
                  qcIncidentId: null,
                };
                const remainingMs = gpsCheckRemainingMs(gpsCheck.lastCheckStartedAt, nowMs);
                const cooldownActive = remainingMs > 0;
                const requireQcIncident =
                  gpsCheck.attemptCount >= GPS_CHECK_QC_INCIDENT_ATTEMPT_THRESHOLD && !gpsCheck.passed;
                const passReady =
                  row.visualPassed &&
                  !requireQcIncident &&
                  (row.hit === "austin" || row.hit === "other" || gpsCheck.passed) &&
                  row.shipmentId == null;
                const failReady = row.hit !== "austin" && row.shipmentId == null && !gpsCheck.passed;
                const hitTone =
                  row.hit === "austin"
                    ? "success"
                    : row.hit === "bad"
                      ? "danger"
                      : row.hit === "none"
                        ? "muted"
                        : "warning";
                return (
                  <tr key={row.serial}>
                    <td className="px-3 py-2 font-mono text-xs">{row.serial}</td>
                    <td className="px-3 py-2">
                      {row.shipmentId != null ? (
                        <Badge tone="muted" className="justify-center px-2 py-1 text-xs">
                          Shipment {row.shipmentId}
                        </Badge>
                      ) : (
                        <Badge
                          tone={row.visualPassed ? "success" : "warning"}
                          className="justify-center px-2 py-1 text-xs"
                        >
                          {row.visualPassed ? "Passed" : "Pending"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {hitsColumnPending ? (
                        <span className="inline-flex min-w-[7.5rem] items-center justify-center py-1.5">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-500" aria-label="Loading wireless hit" />
                        </span>
                      ) : (
                        <Badge
                          tone={hitTone}
                          className="min-w-[7.5rem] justify-center px-3 py-1.5 text-sm font-semibold tracking-tight"
                        >
                          {wirelessHitLabel(row.hit)}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="space-y-1">
                        <p className="text-sm tabular-nums text-slate-800">{gpsCheck.attemptCount}</p>
                        <GpsCheckElapsed lastCheckStartedAt={gpsCheck.lastCheckStartedAt} />
                        {requireQcIncident ? (
                          <div className="flex flex-wrap gap-1">
                            <Button type="button" size="sm" onClick={() => onOpenGpsIncident(row.serial)}>
                              Log QC incident
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => onOpenGpsOverride(row.serial)}
                            >
                              Administrator override
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className={cn("font-normal", FINAL_QC_FLOOR_ACTION_CLASS, "max-xl:min-w-[5.5rem]")}
                        disabled={!passReady || isMutating || hitsColumnPending}
                        onClick={() => onPass(row.serial)}
                      >
                        Pass
                      </Button>
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className={cn(
                          "border-rose-300 bg-rose-50 font-normal text-rose-950 hover:bg-rose-100 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400",
                          FINAL_QC_FLOOR_ACTION_CLASS,
                          "max-xl:min-w-[5.5rem]",
                        )}
                        disabled={!failReady || isMutating || hitsColumnPending || cooldownActive}
                        onClick={() => onGpsFail(row.serial)}
                      >
                        Fail
                      </Button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        className={FINAL_QC_FLOOR_ACTION_CLASS}
                        disabled={isMutating || hitsColumnPending}
                        onClick={() => onRemove(row.serial)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">No devices are waiting for GPS checks.</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function FailureModal({
  reasons,
  onClose,
  onSubmit,
  isSaving,
}: {
  reasons: FailureReason[];
  onClose: () => void;
  onSubmit: (issue: string, notes: string | null) => void;
  isSaving: boolean;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");

  const selectedLabels = useMemo(() => {
    const labels = reasons
      .filter((reason) => selected.includes(reason.value))
      .map((reason) => reason.label);
    const custom = customReason.trim();
    if (custom) labels.push(custom);
    return labels.sort((left, right) => left.localeCompare(right));
  }, [customReason, reasons, selected]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 px-4 py-6">
      <Card className="w-full max-w-2xl">
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitleWithHelp hoverHint="Select one or more failure reasons. You may add a custom reason and notes when needed.">
              Fail Final QC
            </CardTitleWithHelp>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isSaving}>
            Close
          </Button>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-3">
            {reasons.map((reason) => (
              <label
                key={reason.value}
                className="flex min-h-12 items-start gap-3 rounded-md px-2 py-2 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(reason.value)}
                  onChange={(event) => {
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, reason.value]
                        : current.filter((value) => value !== reason.value),
                    );
                  }}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{reason.label}</span>
                  {reason.description ? <span className="block text-xs text-slate-500">{reason.description}</span> : null}
                </span>
              </label>
            ))}
            {reasons.length === 0 ? <p className="text-sm text-slate-600">No failure reasons are configured.</p> : null}
          </div>

          <div>
            <Label>Custom reason</Label>
            <Input value={customReason} onChange={(event) => setCustomReason(event.target.value)} className="mt-1" />
          </div>

          <div>
            <Label>Notes</Label>
            <TextArea value={notes} onChange={(event) => setNotes(event.target.value)} className="mt-1 font-sans" />
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" className={FINAL_QC_FLOOR_ACTION_CLASS} onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              className={FINAL_QC_FLOOR_ACTION_CLASS}
              disabled={selectedLabels.length === 0}
              isLoading={isSaving}
              onClick={() => onSubmit(selectedLabels.join("; "), notes.trim() || null)}
            >
              Submit fail
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export function FinalQcPage() {
  const queryClient = useQueryClient();
  const { openQcIncident } = useQcIncidentModal();
  const laborActive = useLaborActiveSessionContext();
  const laborSessionId = useMemo(
    () => openLaborSessionIdForWirePage(laborActive?.openSession ?? null, FINAL_QC_LABOR_PAGE),
    [laborActive?.openSession],
  );
  const writesAllowed = useHubModuleWrite("manufacturing");
  const [serialInput, setSerialInput] = useState("");
  const [lookupSerial, setLookupSerial] = useState("");
  const [pottingGrade, setPottingGrade] = useState<PottingGrade | null>(null);
  const [result, setResult] = useState<FinalQcResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failModalOpen, setFailModalOpen] = useState(false);
  const [failTargetSerial, setFailTargetSerial] = useState<string | null>(null);
  const [gpsIncidentSerial, setGpsIncidentSerial] = useState<string | null>(null);
  const [gpsOverrideSerial, setGpsOverrideSerial] = useState<string | null>(null);
  const [gpsFailResult, setGpsFailResult] = useState<FinalQcGpsCheckFailResult | null>(null);
  const serialScanInputRef = useRef<HTMLInputElement>(null);
  const lastDeviceLookupErrorRef = useRef<string | null>(null);
  const focusSerialScanInput = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const input = serialScanInputRef.current;
      if (input == null) {
        return;
      }
      input.focus();
      input.select();
    });
  }, []);

  useFloorPageScanCapture({
    enabled: !failModalOpen,
    inputRef: serialScanInputRef,
    onScan: (serial) => {
      setSerialInput(serial);
      setLookupSerial((current) => {
        if (current === serial) {
          void queryClient.invalidateQueries({
            queryKey: ["manufacturing", "final-qc", "device", serial],
          });
        }
        return serial;
      });
    },
  });

  const clearPassFeedback = useCallback((): void => {
    setError(null);
    setResult(null);
  }, []);

  const deviceQuery = useQuery({
    queryKey: ["manufacturing", "final-qc", "device", lookupSerial, laborSessionId ?? null],
    queryFn: () => fetchFinalQcDevice(lookupSerial, { laborSessionId }),
    enabled: lookupSerial.trim() !== "",
    retry: false,
  });

  useEffect(() => {
    if (deviceQuery.data?.wirelessGpsWatch?.enqueued) {
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    }
  }, [deviceQuery.data?.wirelessGpsWatch?.enqueued, queryClient]);

  const failureReasonsQuery = useQuery({
    queryKey: ["manufacturing", "final-qc", "failure-reasons"],
    queryFn: fetchFinalQcFailureReasons,
    enabled: failModalOpen,
  });

  const [wirelessHitsSync, setWirelessHitsSync] = useState<WirelessHitsSyncInfo | null>(null);
  const [foregroundWirelessSync, setForegroundWirelessSync] = useState(false);
  const [manualWirelessRefresh, setManualWirelessRefresh] = useState(false);
  const initialWirelessSyncStarted = useRef(false);
  const foregroundWirelessSyncRef = useRef(false);
  const backgroundWirelessSyncBusy = useRef(false);

  const wirelessChecksQuery = useQuery({
    queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY,
    queryFn: fetchFinalQcWirelessChecksList,
  });

  const runForegroundWirelessSync = useCallback(async () => {
    foregroundWirelessSyncRef.current = true;
    setForegroundWirelessSync(true);
    try {
      const { wirelessHitsSync: syncInfo } = await postFinalQcWirelessHitsSync();
      setWirelessHitsSync(syncInfo);
      await queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    } finally {
      foregroundWirelessSyncRef.current = false;
      setForegroundWirelessSync(false);
    }
  }, [queryClient]);

  useEffect(() => {
    if (wirelessChecksQuery.status !== "success") return;
    if (initialWirelessSyncStarted.current) return;
    initialWirelessSyncStarted.current = true;
    void runForegroundWirelessSync();
  }, [wirelessChecksQuery.status, runForegroundWirelessSync]);

  useEffect(() => {
    const periodMs = 10_000;
    const id = window.setInterval(() => {
      if (backgroundWirelessSyncBusy.current || foregroundWirelessSyncRef.current) return;
      backgroundWirelessSyncBusy.current = true;
      void (async () => {
        try {
          const { wirelessHitsSync: syncInfo } = await postFinalQcWirelessHitsSync();
          setWirelessHitsSync(syncInfo);
          await queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
        } finally {
          backgroundWirelessSyncBusy.current = false;
        }
      })();
    }, periodMs);
    return () => window.clearInterval(id);
  }, [queryClient]);

  const device = deviceQuery.data?.device ?? null;
  const wirelessChecks = wirelessChecksQuery.data?.rows ?? [];
  const deviceIsWatched =
    device != null &&
    (wirelessChecks.some((row) => row.serial === device.deviceLabelSerial) ||
      deviceQuery.data?.wirelessGpsWatch?.enqueued === true);
  const deviceVisualPassed =
    device != null &&
    (wirelessChecks.find((row) => row.serial === device.deviceLabelSerial)?.visualPassed ?? false);
  const alreadyOnWatchList =
    deviceQuery.isSuccess &&
    deviceQuery.data?.wirelessGpsWatch?.required === true &&
    deviceQuery.data?.wirelessGpsWatch?.enqueued === true &&
    deviceQuery.data?.wirelessGpsWatch?.created === false;

  useEffect(() => {
    if (lookupSerial.trim() === "") {
      setPottingGrade(null);
      return;
    }
    setPottingGrade(null);
  }, [lookupSerial]);

  useEffect(() => {
    if (!deviceQuery.isError) {
      lastDeviceLookupErrorRef.current = null;
      return;
    }
    const message =
      deviceQuery.error instanceof Error ? deviceQuery.error.message : String(deviceQuery.error ?? "Lookup failed.");
    if (message === lastDeviceLookupErrorRef.current) {
      return;
    }
    lastDeviceLookupErrorRef.current = message;
    if (!isFinalQcAlreadyPassedLookupError(deviceQuery.error)) {
      playPairFailureSound();
    }
  }, [deviceQuery.isError, deviceQuery.error]);

  useEffect(() => {
    if (failModalOpen || lookupSerial.trim() !== "") {
      return;
    }
    focusSerialScanInput();
  }, [failModalOpen, lookupSerial, focusSerialScanInput]);

  useEffect(() => {
    if (device?.existingPottingGrade) {
      setPottingGrade(device.existingPottingGrade);
    }
  }, [device?.existingPottingGrade]);

  const passMutation = useMutation({
    mutationFn: ({ serial, grade }: { serial: string; grade?: PottingGrade | null }) =>
      passFinalQc({
        serial,
        potting_grade: grade,
        laborSessionId,
      }),
    onSuccess: (data) => {
      playPairSuccessSound();
      setResult(data);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
      if (data.serial) {
        void queryClient.removeQueries({ queryKey: ["manufacturing", "final-qc", "device", data.serial] });
      }
      setLookupSerial("");
      setSerialInput("");
      setPottingGrade(null);
    },
    onError: (mutationError: Error) => {
      playPairFailureSound();
      setError(mutationError.message);
    },
  });

  const visualPassMutation = useMutation({
    mutationFn: (serial: string) => visualPassFinalQc(serial),
    onSuccess: (data) => {
      playPairSuccessSound();
      setLookupSerial("");
      setSerialInput("");
      setPottingGrade(null);
      setError(null);
      setResult({ ok: true, reason: "visual_passed", serial: data.serial });
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      playPairFailureSound();
      setError(mutationError.message);
    },
  });

  const failMutation = useMutation({
    mutationFn: ({
      serial,
      issue,
      notes,
      grade,
    }: {
      serial: string;
      issue: string;
      notes: string | null;
      grade?: PottingGrade | null;
    }) =>
      failFinalQc({
        serial,
        issue,
        issue_notes: notes,
        potting_grade: grade,
        laborSessionId,
      }),
    onSuccess: (data) => {
      playPairSuccessSound();
      setResult(data);
      setError(null);
      setFailModalOpen(false);
      setFailTargetSerial(null);
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
      void deviceQuery.refetch();
    },
    onError: (mutationError: Error) => {
      playPairFailureSound();
      setError(mutationError.message);
    },
  });

  const gpsFailMutation = useMutation({
    mutationFn: (serial: string) => failFinalQcGpsCheck(serial),
    onSuccess: (data) => {
      setGpsFailResult(data);
      setError(data.cooldownBlocked ? data.operatorMessage : null);
      if (data.recorded) {
        playPairFailureSound();
      }
      if (data.requireQcIncident) {
        setGpsIncidentSerial(data.serial);
      }
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      playPairFailureSound();
      setError(mutationError.message);
    },
  });

  const resetGpsWatchMutation = useMutation({
    mutationFn: (serial: string) => watchFinalQcWirelessCheck(serial),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      setError(mutationError.message);
    },
  });

  const removeWirelessMutation = useMutation({
    mutationFn: (serial: string) => removeFinalQcWirelessCheck(serial),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      setError(mutationError.message);
    },
  });

  const lookupError = deviceQuery.error instanceof Error ? deviceQuery.error.message : null;
  const lookupAlreadyPassed =
    deviceQuery.isError && isFinalQcAlreadyPassedLookupError(deviceQuery.error);
  const anyMutationPending =
    passMutation.isPending ||
    visualPassMutation.isPending ||
    failMutation.isPending ||
    gpsFailMutation.isPending ||
    resetGpsWatchMutation.isPending ||
    removeWirelessMutation.isPending;

  function handleCurrentPass(): void {
    if (!device) return;
    if (isWirelessDevice(device)) {
    if (deviceVisualPassed) {
      playPairFailureSound();
      setError(
        "Visual inspection is already recorded for this device. Finish from GPS checks after the reset hit appears.",
      );
      return;
    }
      visualPassMutation.mutate(device.deviceLabelSerial);
      return;
    }
    passMutation.mutate({ serial: device.deviceLabelSerial, grade: pottingGrade });
  }

  function openFailModal(serial: string | null): void {
    setFailTargetSerial(serial);
    setFailModalOpen(true);
  }

  function submitDeviceScan(): void {
    const trimmed = readFloorSerialFromInput(serialScanInputRef.current, serialInput);
    setSerialInput(trimmed);
    if (trimmed === "") {
      return;
    }
    if (trimmed === lookupSerial) {
      void queryClient.invalidateQueries({
        queryKey: ["manufacturing", "final-qc", "device", trimmed],
      });
    }
    setLookupSerial(trimmed);
  }

  const passResultMessage = resultMessage(result);
  const scanStatusMessage = error ?? lookupError ?? passResultMessage;
  const scanStatusIsError =
    error != null ||
    (lookupError != null && !lookupAlreadyPassed) ||
    (result != null && (result.reason === "failed" || result.reason === "potting_rework_failed"));

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageTitleWithHelp hoverHint={FINAL_QC_PAGE_HELP}>Final QC</PageTitleWithHelp>
          <p className="mt-2 text-sm">
            <Link to="/manufacturing/final-qc/mistaken-pass" className="font-medium text-sky-700 underline hover:text-sky-900">
              Mistaken Final QC pass
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DeviceLookupModalTrigger />
          <LaborStationOperatorPresence stationPage="final-qc" />
        </div>
      </header>

      <FinalQcLaborTrackerShared />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle>Scan device</CardTitle>
            {scanStatusMessage ? (
              <p
                role={scanStatusIsError ? "alert" : "status"}
                aria-live={scanStatusIsError ? "assertive" : "polite"}
                className={
                  scanStatusIsError
                    ? "rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm text-rose-800"
                    : lookupAlreadyPassed
                      ? "rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm text-sky-900"
                      : "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-800"
                }
              >
                {scanStatusMessage}
              </p>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault();
              submitDeviceScan();
            }}
          >
            <Input
              ref={serialScanInputRef}
              id="final-qc-scan-device"
              name="serial"
              value={serialInput}
              onChange={(event) => {
                const raw = event.target.value;
                if (floorSerialInputHasScannerTerminator(raw)) {
                  const nextSerial = normalizeFloorSerialInput(raw);
                  setSerialInput(nextSerial);
                  if (nextSerial !== "") {
                    if (nextSerial === lookupSerial) {
                      void queryClient.invalidateQueries({
                        queryKey: ["manufacturing", "final-qc", "device", nextSerial],
                      });
                    }
                    setLookupSerial(nextSerial);
                  }
                  return;
                }
                const next = raw;
                if (next !== serialInput) {
                  clearPassFeedback();
                }
                setSerialInput(next);
              }}
              onKeyDown={(event) => handleFloorSerialEnterKey(event, submitDeviceScan)}
              placeholder="Device label, box serial, or serial id"
              {...FLOOR_SCAN_INPUT_FIELD_PROPS}
              {...FLOOR_PAGE_SCAN_TARGET_PROPS}
            />
            <Button type="submit" className="min-h-12 shrink-0 px-6 text-base" isLoading={deviceQuery.isFetching}>
              Submit
            </Button>
          </form>
        </CardBody>
      </Card>

      {alreadyOnWatchList ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950" role="status">
          This device is already on the GPS checks watch list. The scan did not reset the watch or clear visual
          inspection or hit progress. Use Reset GPS watch only after a factory reset.
        </p>
      ) : null}

      {wirelessHitsSync?.ok === false ? (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
          role="alert"
        >
          {wirelessHitsSync.message}
        </div>
      ) : null}

      <ReadOnlyHoverLayer
        active={!writesAllowed}
        hoverHint="Your role has read-only access to the Manufacturing hub module."
        layout="block"
      >
        <div className="grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
          <div
            className={cn(
              "max-xl:order-2 max-xl:sticky max-xl:bottom-0 max-xl:z-10",
              "max-xl:border-t max-xl:border-slate-200 max-xl:bg-slate-50/95 max-xl:pt-2 max-xl:backdrop-blur-sm",
              "max-xl:pb-[max(0.75rem,env(safe-area-inset-bottom))]",
            )}
          >
            <FinalQcWorkflowPanel
              device={device}
              writesAllowed={writesAllowed}
              pottingGrade={pottingGrade}
              setPottingGrade={setPottingGrade}
              isSaving={anyMutationPending}
              isResettingGpsWatch={resetGpsWatchMutation.isPending}
              watched={deviceIsWatched}
              alreadyOnWatchList={alreadyOnWatchList}
              visualPassed={deviceVisualPassed}
              onResetGpsWatch={() => {
                if (device) resetGpsWatchMutation.mutate(device.deviceLabelSerial);
              }}
              onPass={handleCurrentPass}
              onFail={() => openFailModal(device?.deviceLabelSerial ?? null)}
            />
          </div>

          <div className="max-xl:order-1 space-y-4 max-xl:pb-56">
            {device ? (
              <Card>
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle>{device.deviceLabelSerial}</CardTitle>
                    <p className="text-sm text-slate-500">{deviceSubtitle(device)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isWirelessDevice(device) ? <Badge tone="info">Wireless checks</Badge> : null}
                    {device.pottingRework ? <Badge tone="info">Potting rework</Badge> : null}
                    {needsPottingGrade(device) ? <Badge tone="warning">Potting grade required</Badge> : null}
                    {device.shipmentId != null ? <Badge tone="muted">Already in shipment</Badge> : null}
                  </div>
                </CardHeader>
                <CardBody className="space-y-4">
                  <dl className="grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Finished SKU</dt>
                      <dd className="font-mono text-slate-900">{device.finishedSku ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Batch item</dt>
                      <dd className="text-slate-900">{device.batchItemId ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Shipment</dt>
                      <dd className="text-slate-900">{device.shipmentId ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">QC incident</dt>
                      <dd className="text-slate-900">
                        {device.pottingRework?.incidentId != null ? (
                          <button
                            type="button"
                            className="font-medium text-sky-700 underline hover:text-sky-900"
                            onClick={() => openQcIncident(device.pottingRework!.incidentId, "view")}
                          >
                            #{device.pottingRework.incidentId}
                          </button>
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Reason not in batch</dt>
                      <dd className="text-slate-900">{device.reasonNotInBatch ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">IMEI</dt>
                      <dd className="font-mono text-slate-900">{device.imei ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Potting grade</dt>
                      <dd className="text-slate-900">
                        {pottingGrade ?? device.pottingRework?.regradePottingGrade ?? device.existingPottingGrade ?? "—"}
                      </dd>
                    </div>
                  </dl>
                </CardBody>
              </Card>
            ) : null}

            <WirelessChecksTable
              rows={wirelessChecks}
              hitsColumnPending={foregroundWirelessSync}
              refreshButtonPending={manualWirelessRefresh}
              isMutating={anyMutationPending}
              onRefresh={() => {
                setManualWirelessRefresh(true);
                void runForegroundWirelessSync().finally(() => {
                  setManualWirelessRefresh(false);
                });
              }}
              onPass={(serial) => passMutation.mutate({ serial, grade: null })}
              onGpsFail={(serial) => gpsFailMutation.mutate(serial)}
              onOpenGpsIncident={(serial) => setGpsIncidentSerial(serial)}
              onOpenGpsOverride={(serial) => setGpsOverrideSerial(serial)}
              onRemove={(serial) => removeWirelessMutation.mutate(serial)}
            />
          </div>
        </div>
      </ReadOnlyHoverLayer>

      {failModalOpen ? (
        <FailureModal
          reasons={failureReasonsQuery.data?.rows ?? []}
          onClose={() => {
            setFailModalOpen(false);
            setFailTargetSerial(null);
          }}
          isSaving={failMutation.isPending}
          onSubmit={(issue, notes) => {
            const serial = failTargetSerial ?? device?.deviceLabelSerial ?? lookupSerial;
            const grade = serial === device?.deviceLabelSerial ? pottingGrade : null;
            failMutation.mutate({ serial, issue, notes, grade });
          }}
        />
      ) : null}
      <FinalQcGpsIncidentModal
        open={gpsIncidentSerial != null}
        serial={gpsIncidentSerial}
        attemptNumber={
          gpsFailResult?.serial === gpsIncidentSerial
            ? gpsFailResult.attemptNumber ?? gpsFailResult.episode.attemptCount
            : wirelessChecks.find((row) => row.serial === gpsIncidentSerial)?.gpsCheck?.attemptCount ?? 5
        }
        failureModeId={
          gpsFailResult?.serial === gpsIncidentSerial
            ? gpsFailResult.suggestedFailureModeId
            : suggestedGpsFailureMode(wirelessChecks.find((row) => row.serial === gpsIncidentSerial)?.hit, wirelessChecksQuery.data?.gpsFailureModes)?.id ?? null
        }
        failureModeName={
          gpsFailResult?.serial === gpsIncidentSerial
            ? gpsFailResult.suggestedFailureModeName
            : suggestedGpsFailureMode(wirelessChecks.find((row) => row.serial === gpsIncidentSerial)?.hit, wirelessChecksQuery.data?.gpsFailureModes)?.name ?? null
        }
        onClose={() => setGpsIncidentSerial(null)}
        onSubmitted={() => {
          setGpsIncidentSerial(null);
          void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
        }}
      />
      <FinalQcGpsOverrideModal
        open={gpsOverrideSerial != null}
        serial={gpsOverrideSerial}
        attemptCount={
          wirelessChecks.find((row) => row.serial === gpsOverrideSerial)?.gpsCheck?.attemptCount ??
          gpsFailResult?.episode.attemptCount ??
          5
        }
        onClose={() => setGpsOverrideSerial(null)}
        onSuccess={() => {
          setGpsOverrideSerial(null);
          void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
        }}
      />
    </div>
  );
}
